import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { toast } from 'sonner';
import {
  Zap,
  Target,
  TrendingUp,
  Users,
  Calendar,
  BarChart3,
  Settings,
  Plus,
  ChevronRight,
  Flame,
  Trophy,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter,
  Mail,
  MessageCircle,
  Megaphone,
  PenTool,
  Send,
  Eye,
  Heart,
  MousePointer,
  DollarSign } from
'lucide-react';
import { Link } from 'react-router-dom';
import ConsistencyHeatmap from '../components/ConsistencyHeatmap';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Provider icons map
const providerIcons = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
  x: Twitter,
  threads: MessageCircle,
  tiktok: Megaphone,
  whatsapp_business: MessageCircle,
  email_gmail: Mail,
  email_outlook: Mail,
  email_mailchimp: Mail,
  google_ads: BarChart3,
  meta_ads: BarChart3
};

export const CNSDashboard = () => {const { t } = useTranslation("cns");
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    fetchProviders();
    fetchWorkspaces();
  }, []);

  useEffect(() => {
    if (selectedWorkspace) {
      fetchDashboard(selectedWorkspace.id);
    }
  }, [selectedWorkspace]);

  const fetchProviders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cns/accounts/providers`);
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      console.error('Failed to fetch providers');
    }
  };

  const fetchWorkspaces = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cns/workspaces`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setWorkspaces(data.workspaces || []);
        if (data.workspaces?.length > 0) {
          setSelectedWorkspace(data.workspaces[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch workspaces');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async (workspaceId) => {
    try {
      const res = await fetch(`${API_URL}/api/cns/workspaces/${workspaceId}/dashboard`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard');
    }
  };

  const createDemoWorkspace = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cns/workspaces`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: 'My Brand',
          slug: 'my-brand-' + Date.now(),
          description: 'My social media workspace',
          timezone: 'America/Sao_Paulo',
          default_language: 'pt-BR'
        })
      });
      if (res.ok) {
        const workspace = await res.json();
        toast.success('Workspace created!');
        setWorkspaces([workspace, ...workspaces]);
        setSelectedWorkspace(workspace);
      }
    } catch (err) {
      toast.error('Failed to create workspace');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="cns-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
              <Zap className="h-7 w-7 text-white" />
            </div>{t("socialMediaCns", "Social Media CNS")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("centralNervousSystem", "Central Nervous System for Marketing, SEO & Growth")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" asChild>
            <Link to="/cns/scheduler">
              <Calendar className="h-4 w-4" />{t("calendar", "Calendar")}

            </Link>
          </Button>
          <Button className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600" asChild>
            <Link to="/cns/content/new">
              <Plus className="h-4 w-4" />{t("createContent", "Create Content")}

            </Link>
          </Button>
        </div>
      </div>

      {/* No Workspace State */}
      {workspaces.length === 0 ?
      <Card className="card-elevated border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
              <Megaphone className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t("welcomeToSocial", "Welcome to Social Media CNS")}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("createYourFirst", "Create your first workspace to start managing your brand's social media presence across all channels.")}

          </p>
            <Button
            className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600"
            onClick={createDemoWorkspace}>
            
              <Plus className="h-4 w-4" />{t("createWorkspace", "Create Workspace")}

          </Button>
          </CardContent>
        </Card> :

      <>
          {/* Gamification Stats */}
          {dashboard &&
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Level & Points */}
              <Card className="card-elevated bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Trophy className="h-5 w-5 text-amber-600" />
                    </div>
                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">{t("level", "Level")}
                  {dashboard.gamification?.level || 1}
                    </Badge>
                  </div>
                  <h3 className="text-2xl font-bold text-amber-700">
                    {(dashboard.gamification?.points || 0).toLocaleString()}{t("pts", "pts")}
              </h3>
                  <p className="text-sm text-amber-600 mt-1">{t("totalPointsEarned", "Total points earned")}</p>
                  <Progress value={86} className="mt-3 h-2" />
                  <p className="text-xs text-amber-500 mt-1">{t("86ToLevel", "86% to Level")}{(dashboard.gamification?.level || 1) + 1}</p>
                </CardContent>
              </Card>

              {/* Streak */}
              <Card className="card-elevated bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Flame className="h-5 w-5 text-red-600" />
                    </div>
                    <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">{t("record", "Record:")}
                  {dashboard.gamification?.longest_streak || 0}
                    </Badge>
                  </div>
                  <h3 className="text-2xl font-bold text-red-700">
                    {dashboard.gamification?.streak || 0}{t("days", "days")}
              </h3>
                  <p className="text-sm text-red-600 mt-1">{t("currentStreak", "Current streak")}</p>
                  <div className="flex gap-1 mt-3">
                    {[...Array(7)].map((_, i) =>
                <div
                  key={i}
                  className={`flex-1 h-2 rounded ${i < Math.min(dashboard.gamification?.streak || 0, 7) ? 'bg-red-500' : 'bg-red-200'}`} />

                )}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Goal */}
              <Card className="card-elevated bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Target className="h-5 w-5 text-emerald-600" />
                    </div>
                    {dashboard.queue?.pending_jobs === 0 &&
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        <CheckCircle className="h-3 w-3 mr-1" />{t("targetMet", "TARGET MET")}

                </Badge>
                }
                  </div>
                  <h3 className="text-2xl font-bold text-emerald-700">
                    {dashboard.content?.total || 0} / {selectedWorkspace?.posts_goal_weekly || 10}
                  </h3>
                  <p className="text-sm text-emerald-600 mt-1">{t("weeklyGoal", "Weekly goal")}</p>
                  <Progress
                value={Math.min((dashboard.content?.total || 0) / (selectedWorkspace?.posts_goal_weekly || 10) * 100, 100)}
                className="mt-3 h-2" />
              
                </CardContent>
              </Card>

              {/* Engagement */}
              <Card className="card-elevated bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                    </div>
                    <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">{t("30Days", "30 days")}

                </Badge>
                  </div>
                  <h3 className="text-2xl font-bold text-blue-700">
                    {(dashboard.metrics_30d?.engagement || 0).toLocaleString()}
                  </h3>
                  <p className="text-sm text-blue-600 mt-1">{t("totalEngagement", "Total engagement")}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-blue-600">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {((dashboard.metrics_30d?.impressions || 0) / 1000).toFixed(1)}K
                    </span>
                    <span className="flex items-center gap-1">
                      <MousePointer className="h-3 w-3" />
                      {(dashboard.metrics_30d?.clicks || 0).toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
        }

          {/* Main Content Grid */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Heatmap - Full Width */}
            <div className="lg:col-span-2">
              {selectedWorkspace &&
            <ConsistencyHeatmap workspaceId={selectedWorkspace.id} />
            }
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Connected Accounts */}
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-emerald-600" />{t("connectedAccounts", "Connected Accounts")}

                  </span>
                    <Button variant="ghost" size="sm" className="text-emerald-600">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.accounts?.total === 0 ?
                <div className="text-center py-6">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center">
                        <Users className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{t("noAccountsConnected", "No accounts connected yet")}

                  </p>
                      <Button size="sm" variant="outline" className="gap-1">
                        <Plus className="h-3 w-3" />{t("connectAccount", "Connect Account")}

                  </Button>
                    </div> :

                <div className="space-y-3">
                      {providers.slice(0, 5).map((provider) => {
                    const Icon = providerIcons[provider.id] || MessageCircle;
                    return (
                      <div
                        key={provider.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                        
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-muted">
                                <Icon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{provider.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {provider.supports_publish ? 'Can publish' : 'Read-only'}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm">{t("connect", "Connect")}

                        </Button>
                          </div>);

                  })}
                    </div>
                }
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-emerald-600" />{t("quickActions", "Quick Actions")}

                </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="outline" className="w-full justify-start gap-2" asChild>
                    <Link to="/cns/content/new">
                      <PenTool className="h-4 w-4" />{t("createPost", "Create Post")}

                  </Link>
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2" asChild>
                    <Link to="/cns/campaigns">
                      <Megaphone className="h-4 w-4" />{t("newCampaign", "New Campaign")}

                  </Link>
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2" asChild>
                    <Link to="/cns/calendar">
                      <Calendar className="h-4 w-4" />{t("scheduleContent", "Schedule Content")}

                  </Link>
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2" asChild>
                    <Link to="/cns/analytics">
                      <BarChart3 className="h-4 w-4" />{t("viewAnalytics", "View Analytics")}

                  </Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Publishing Queue */}
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5 text-emerald-600" />{t("publishingQueue", "Publishing Queue")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t("pending", "Pending")}</span>
                      <Badge variant="outline">{dashboard?.queue?.pending_jobs || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 text-amber-500" />{t("needsAttention", "Needs Attention")}

                    </span>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {dashboard?.queue?.needs_attention || 0}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      }
    </div>);

};

export default CNSDashboard;
